import { onUnmounted, watch, type ComputedRef, type Ref } from 'vue'
import { useToastViewportStore, type ToastViewportPreset } from './useToastViewportStore'

interface ToastViewportScopeOptions {
  priority?: number
}

/**
 * Keeps the global toast container in the viewport preset that matches
 * the current UI context, for example while a right panel is open.
 *
 * Use this in components that temporarily change where global toasts
 * should appear. When `open` becomes `true`, the composable registers
 * the given preset in the shared toast viewport store. When `open`
 * becomes `false` or the component is unmounted, the registration is removed.
 *
 * This lets all existing `useToast()` calls continue to work as usual,
 * while the single global toast container moves to a safer position for
 * the active overlay.
 */
export const useToastViewportScope = (
  open: Ref<boolean> | ComputedRef<boolean>,
  preset: ToastViewportPreset,
  options: ToastViewportScopeOptions = {},
) => {
  const { registerToastViewport } = useToastViewportStore()

  let unregister: (() => void) | null = null

  const releaseRegistration = () => {
    unregister?.()
    unregister = null
  }

  const ensureRegistration = () => {
    if (unregister) return

    unregister = registerToastViewport(preset, {
      priority: options.priority,
    })
  }

  watch(
    open,
    (isOpen) => {
      if (isOpen) {
        ensureRegistration()
        return
      }

      if (!unregister) return
      releaseRegistration()
    },
    { immediate: true },
  )

  onUnmounted(() => {
    releaseRegistration()
  })
}
